import { useEffect, useRef, useState } from 'react';
import { INGREDIENTS_BY_ID } from '../../data/index.js';
import { AISLE_LABELS, AISLE_ORDER } from '../../domain/aisles.js';
import {
  ALL_TAGS, CUISINES, PROTEINS, UNITS_FOR, draftProblems, draftToMeal, eachLabel, emptyDraft,
  isApproximate, isBlankDraft, mealToDraft, type DraftLine, type EntryUnit, type RecipeDraft,
} from '../../domain/recipeDraft.js';
import type { AisleCategory } from '../../domain/types.js';
import { IngredientSearch } from '../components/IngredientSearch.js';
import { pasteRecipe } from '../recipes/parseClient.js';
import { newCustomMealId, type CustomMeal } from '../store/library.js';
import { accessToken } from '../sync/client.js';
import { deviceId } from '../sync/device.js';

const numberOrNull = (raw: string): number | null => {
  const n = Number(raw);
  return raw.trim() === '' || !Number.isFinite(n) ? null : n;
};

export function RecipeForm({
  editing,
  onSaved,
  onCancel,
}: {
  editing: CustomMeal | null;
  onSaved: (meal: CustomMeal) => void;
  onCancel: () => void;
}): React.JSX.Element {
  const [draft, setDraft] = useState<RecipeDraft>(() =>
    editing ? mealToDraft(editing, editing.serves) : emptyDraft());
  const [attempted, setAttempted] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const [source, setSource] = useState<CustomMeal['source']>(editing?.source ?? 'form');
  const [sourceUrl, setSourceUrl] = useState<string | null>(editing?.sourceUrl ?? null);
  const [token, setToken] = useState<string | null>(null);
  const [pasteText, setPasteText] = useState('');
  const [pasting, setPasting] = useState(false);
  const [pasteError, setPasteError] = useState<string | null>(null);
  const [warnings, setWarnings] = useState<string[]>([]);
  // Its own text, so clearing the box mid-edit doesn't snap it back to 30.
  const [minutesText, setMinutesText] = useState(() => String(draft.effortMinutes));

  useEffect(() => {
    let live = true;
    void accessToken().then((t) => { if (live) setToken(t); }).catch(() => undefined);
    return () => { live = false; };
  }, []);

  const draftRef = useRef(draft);
  draftRef.current = draft;

  const fill = async (): Promise<void> => {
    if (!token || pasteText.trim() === '') return;
    setPasting(true);
    setPasteError(null);
    try {
      // Fetched now, not reused from mount: a form left open for an hour holds
      // an expired token. The mount-time one only drives the button's label.
      const fresh = await accessToken();
      if (!fresh) {
        setToken(null);
        setPasteError('Sign in again to paste recipes.');
        return;
      }
      const outcome = await pasteRecipe(pasteText, fresh);
      if (!outcome.ok) {
        setPasteError(outcome.message);
      } else if (!isBlankDraft(draftRef.current)) {
        setPasteError("You started typing, so the pasted recipe wasn't used.");
      } else {
        setDraft(outcome.draft);
        setMinutesText(String(outcome.draft.effortMinutes));
        setWarnings(outcome.warnings);
        setSource(outcome.source);
        setSourceUrl(outcome.sourceUrl);
      }
    } catch {
      setPasteError('Something went wrong reading that. Type the recipe in, or try again.');
    } finally {
      setPasting(false);
    }
  };

  const problems = draftProblems(draft, INGREDIENTS_BY_ID);
  const set = (patch: Partial<RecipeDraft>): void => setDraft((d) => ({ ...d, ...patch }));
  const setLine = (index: number, line: DraftLine): void =>
    setDraft((d) => ({ ...d, lines: d.lines.map((l, i) => (i === index ? line : l)) }));
  const removeLine = (index: number): void =>
    setDraft((d) => ({ ...d, lines: d.lines.filter((_, i) => i !== index) }));

  const save = (): void => {
    if (problems.length > 0) {
      setAttempted(true);
      return;
    }
    const id = editing?.id ?? newCustomMealId();
    const minutes = numberOrNull(minutesText);
    // Whole minutes within the input's own 5 to 480; blank or nonsense is 30.
    const effortMinutes = minutes !== null && minutes > 0 ? Math.min(480, Math.max(5, Math.round(minutes))) : 30;
    onSaved({
      ...draftToMeal({ ...draft, effortMinutes }, INGREDIENTS_BY_ID, id),
      serves: draft.serves,
      // Editing never un-archives: Save is not "restore".
      archived: editing?.archived ?? false,
      source,
      sourceUrl,
      createdBy: editing?.createdBy ?? deviceId(),
    });
  };

  return (
    <form className="recipe-form" onSubmit={(e) => { e.preventDefault(); save(); }}>
      {!editing && isBlankDraft(draft) ? (
        <div className="paste-box">
          <label className="field">
            <span>Paste a recipe or a link</span>
            <textarea rows={3} value={pasteText} onChange={(e) => setPasteText(e.target.value)} />
          </label>
          <button
            type="button" disabled={!token || pasting || pasteText.trim() === ''}
            onClick={() => void fill()}
          >
            {!token ? 'Sign in to paste recipes' : pasting ? 'Reading it…' : 'Fill in for me'}
          </button>
        </div>
      ) : null}
      {pasteError ? <p className="nudge">{pasteError}</p> : null}
      {warnings.length > 0 ? (
        <ul className="paste-warnings" role="status">
          {warnings.map((w, i) => <li key={`${i}:${w}`}>{w}</li>)}
        </ul>
      ) : null}
      <label className="field">
        <span>Recipe name</span>
        <input value={draft.name} onChange={(e) => set({ name: e.target.value })} />
      </label>

      <label className="field field-short">
        <span>Serves</span>
        <input
          type="number" inputMode="numeric" min={1} max={20}
          value={Number.isNaN(draft.serves) ? '' : draft.serves}
          onChange={(e) => set({ serves: e.target.valueAsNumber })}
        />
      </label>
      <p className="hint">Type amounts for the whole recipe, as it&rsquo;s written.</p>

      <ul className="recipe-lines">
        {draft.lines.map((line, index) => {
          const key = `${index}:${line.kind === 'ingredient' ? line.ingredientId : line.label}`;
          if (line.kind === 'ingredient') {
            const ingredient = INGREDIENTS_BY_ID.get(line.ingredientId);
            const name = ingredient?.name ?? line.ingredientId;
            const units = ingredient ? UNITS_FOR[ingredient.baseUnit] : [line.unit];
            return (
              <li key={key} className="recipe-line">
                <span className="recipe-line-name">{name}</span>
                <input
                  aria-label={`Amount of ${name}`} type="number" inputMode="decimal" min={0} step="any"
                  value={line.amount ?? ''}
                  onChange={(e) => setLine(index, { ...line, amount: numberOrNull(e.target.value) })}
                />
                <select
                  aria-label={`Unit for ${name}`} value={line.unit}
                  onChange={(e) => setLine(index, { ...line, unit: e.target.value as EntryUnit })}
                >
                  {units.map((u) => (
                    <option key={u} value={u}>
                      {u === 'each' ? eachLabel(line.ingredientId) : u}
                      {ingredient && isApproximate(ingredient.baseUnit, u) ? ' (≈)' : ''}
                    </option>
                  ))}
                </select>
                <button type="button" className="why" aria-label={`Remove ${name}`} onClick={() => removeLine(index)}>
                  &times;
                </button>
              </li>
            );
          }
          return (
            <li key={key} className="recipe-line">
              <span className="recipe-line-name">{line.label} <small>extra</small></span>
              <input
                aria-label={`Amount of ${line.label}`} type="number" inputMode="decimal" min={0} step="any"
                placeholder="some" value={line.amount ?? ''}
                onChange={(e) => setLine(index, { ...line, amount: numberOrNull(e.target.value) })}
              />
              <input
                aria-label={`Unit for ${line.label}`} placeholder="tbsp, jar…" value={line.unitLabel}
                onChange={(e) => setLine(index, { ...line, unitLabel: e.target.value })}
              />
              <select
                aria-label={`Aisle for ${line.label}`} value={line.aisle}
                onChange={(e) => setLine(index, { ...line, aisle: e.target.value as AisleCategory })}
              >
                {AISLE_ORDER.aldi.map((a) => <option key={a} value={a}>{AISLE_LABELS[a]}</option>)}
              </select>
              <button type="button" className="why" aria-label={`Remove ${line.label}`} onClick={() => removeLine(index)}>
                &times;
              </button>
            </li>
          );
        })}
      </ul>

      <IngredientSearch
        onPick={(ingredientId) => {
          const base = INGREDIENTS_BY_ID.get(ingredientId)?.baseUnit ?? 'g';
          set({ lines: [...draft.lines, {
            kind: 'ingredient', ingredientId, amount: null, unit: UNITS_FOR[base][0] ?? 'g',
            prepNote: null, optional: false,
          }] });
        }}
        onExtra={(label) => set({ lines: [...draft.lines, {
          kind: 'extra', label, amount: null, unitLabel: '', aisle: 'ambient',
        }] })}
      />

      <label className="field">
        <span>Method (one step per line, optional)</span>
        <textarea
          rows={4} value={draft.method.join('\n')}
          onChange={(e) => set({ method: e.target.value.split('\n') })}
        />
      </label>

      <button type="button" className="ghost" aria-expanded={moreOpen} onClick={() => setMoreOpen((v) => !v)}>
        More details
      </button>
      {moreOpen ? (
        <div className="recipe-more">
          <label className="field field-short">
            <span>Minutes</span>
            <input
              type="number" inputMode="numeric" min={5} max={480} value={minutesText}
              onChange={(e) => setMinutesText(e.target.value)}
            />
          </label>
          <label className="field">
            <span>Cuisine</span>
            <select value={draft.cuisine} onChange={(e) => set({ cuisine: e.target.value })}>
              {CUISINES.map((c) => <option key={c} value={c}>{c[0]?.toUpperCase()}{c.slice(1)}</option>)}
            </select>
          </label>
          <label className="field">
            <span>Main protein</span>
            <select value={draft.mainProtein} onChange={(e) => set({ mainProtein: e.target.value })}>
              {PROTEINS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
            </select>
          </label>
          <div className="chips" role="group" aria-label="Tags">
            {ALL_TAGS.map((tag) => (
              <button
                key={tag} type="button" aria-pressed={draft.tags.includes(tag)}
                onClick={() => set({
                  tags: draft.tags.includes(tag) ? draft.tags.filter((t) => t !== tag) : [...draft.tags, tag],
                })}
              >
                {tag}
              </button>
            ))}
          </div>
        </div>
      ) : null}

      {attempted && problems.length > 0 ? (
        <ul className="problems" role="alert">
          {problems.map((p) => <li key={p}>{p}</li>)}
        </ul>
      ) : null}

      <button type="submit" className="primary">Save recipe</button>
      <button type="button" className="ghost" onClick={onCancel}>Cancel</button>
    </form>
  );
}
